/* eslint-disable max-lines */
/* eslint-disable unicorn/prevent-abbreviations */
import { PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { addBrowserListener, openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useHelloSignEmbeddedFlow } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignEmbeddedFlow";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { DocumentStatus } from "../../../../../Accounts/Documents/types";
import { APP_V2_APP_EVENTS, logError } from "../../../../../lib/analytics";
import { useDefinedWorker } from "../../../../../Worker/useDefinedWorker";
import { PageHeaderTopRow } from "../../../../components/PageHeaderTopRow";
import { PageWrapper } from "../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../components/StickyScrollPageHeader";
import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import { useGetRequirements } from "../../../api/useGetRequirements";
import { DocumentsErrorPage } from "../../../ErrorPage";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
} from "../../../paths";
import { DocumentSubmissionInProgressBottomSheet } from "../../../Submission/InProgressBottomSheet";
import { DocumentUploadFooter } from "../../Footer";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useDocumentDetailsActions } from "../../useDocumentDetailsActions";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { useHelloSignFallbackUrl } from "../HelloSign/useHelloSignFallbackUrl";
import { ESignWorkflowSessionStatus } from "../PandaDoc/constants";
import { PandaDocEmbeddedSessionLoadingBottomSheet } from "../PandaDoc/EmbeddedSessionLoadingBottomSheet";
import { getDocumentStatusOfESignWorkflowSession } from "../PandaDoc/getDocumentStatusOfESignWorkflowSession";
import { PandaDocEmbeddedSigning } from "../PandaDoc/PandaDocEmbeddedSigning";
import { usePandaDocSigningEnabled } from "../PandaDoc/usePandaDocSigningEnabled";
import { useSignableESignWorkflowSession } from "../PandaDoc/useSignableESignWorkflowSession";
import { useUpdateESignWorkflowSession } from "../PandaDoc/useUpdateESignWorkflowSession";

export function DocumentDetailsCompositePage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const history = useHistory();

  const worker = useDefinedWorker();
  const workerId = worker.userId;

  const helloSignIframeContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const helloSignFormModalState = useModalState();
  const pandaDocModalState = useModalState();
  const loadingPandaDocModalState = useModalState();
  const submittingDocumentModalState = useModalState();
  const { openModal: openSubmittingDocumentModal } = submittingDocumentModalState;
  const queryClient = useQueryClient();
  const { showInfoToast } = useToast();
  const shouldUseHelloSignFallbackUrl = useHelloSignFallbackUrl();

  const { requirement, status, requirementId } = workerRequirement;
  const { name, compositeConfig } = requirement?.attributes ?? {};
  const { requirements: compositeRequirements } = compositeConfig ?? {};
  const compositeHelloSignRequirement = compositeRequirements?.[0];
  const compositeDocumentUploadRequirement = compositeRequirements?.[1];
  const helloSignRequirementCtaText = compositeHelloSignRequirement?.ctaText;
  const documentUploadRequirementCtaText = compositeDocumentUploadRequirement?.ctaText;

  const isPandaDocSigningEnabled = usePandaDocSigningEnabled(requirementId);

  const { uploadDocument, deleteDocument } = useDocumentDetailsActions();

  const { data: helloSignRequirement, isLoading: isLoadingHelloSignRequirement } =
    useGetRequirements(
      {
        filter: {
          id: compositeHelloSignRequirement?.requirementId,
        },
      },
      {
        enabled: isDefined(compositeHelloSignRequirement?.requirementId),
      }
    );

  const helloSignConfig = helloSignRequirement?.data?.[0].attributes.helloSignConfig;
  const fallbackStaticUrl = helloSignConfig?.fallbackStaticUrl;
  const isEmbeddedFlowEnabled = helloSignConfig?.isEmbeddedFlowEnabled;

  const signRequirementId = compositeHelloSignRequirement?.requirementId;

  const {
    mutate: loadSignableESignWorkflowSession,
    data: signableESignWorkflowSession,
    isLoading: isLoadingSignableESignWorkflowSession,
  } = useSignableESignWorkflowSession({
    // We expect signRequirementId to be always present
    // but passing empty string to make TS happy and also in offchance i am wrong,
    // to fail fast in runtime and alert us
    requirementId: signRequirementId ?? "",
    onError: useCallback(
      (error: unknown) => {
        pandaDocModalState.closeModal();
        logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
          error,
          metadata: { requirementId, signRequirementId },
        });
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      [pandaDocModalState, history, requirementId, signRequirementId]
    ),
  });
  const { mutateAsync: updateESignWorkflowSession } = useUpdateESignWorkflowSession();

  const onPandaDocCompleted = useCallback(async () => {
    pandaDocModalState.closeModal();
    submittingDocumentModalState.openModal();
    let modalPath = DOCUMENT_SUBMISSION_IN_REVIEW_PATH;
    try {
      // In runtime we will always have a signableESignWorkflowSession and signRequirementId
      // This is for TypeScript to be happy
      if (!isDefined(signableESignWorkflowSession) || !isDefined(signRequirementId)) {
        throw new Error("signableESignWorkflowSession or signRequirementId is not defined");
      }

      const updatedSessionResponse = await updateESignWorkflowSession({
        requirementId: signRequirementId,
        sessionId: signableESignWorkflowSession.id,
        attributesToUpdate: { status: ESignWorkflowSessionStatus.SIGNED },
      });

      const documentStatus = updatedSessionResponse
        ? await getDocumentStatusOfESignWorkflowSession({
            data: updatedSessionResponse.data,
            included: updatedSessionResponse.included,
          })
        : await getDocumentStatusOfESignWorkflowSession({
            requirementId: signRequirementId,
            workerId,
          });

      modalPath =
        documentStatus === DocumentStatus.APPROVED ? DOCUMENT_SUBMISSION_APPROVED_PATH : modalPath;
    } catch (error: unknown) {
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error,
        metadata: { requirementId, signRequirementId },
      });
      void invalidateWorkerRequirements(queryClient);
      submittingDocumentModalState.closeModal();
      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
          requirementId,
        })
      );
      return;
    }

    void invalidateWorkerRequirements(queryClient);
    submittingDocumentModalState.closeModal();
    history.replace(
      generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
        requirementId,
      })
    );
  }, [
    workerId,
    requirementId,
    history,
    pandaDocModalState,
    signRequirementId,
    queryClient,
    submittingDocumentModalState,
    signableESignWorkflowSession,
    updateESignWorkflowSession,
  ]);

  const { isCreatingHelloSignSession, isSubmittingHelloSignDocument, startHelloSignEmbeddedFlow } =
    useHelloSignEmbeddedFlow({
      requirementId,
      compositeChildRequirementId: compositeHelloSignRequirement?.requirementId,
      onSuccess: (isDocumentApproved) => {
        const modalPath = isDocumentApproved
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

        void invalidateWorkerRequirements(queryClient);

        submittingDocumentModalState.closeModal();
        helloSignFormModalState.closeModal();

        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
            requirementId,
          })
        );
      },
      onError: () => {
        helloSignFormModalState.closeModal();
        submittingDocumentModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      helloSignIframeContainerRef,
      onHelloSignClientCancel: () => {
        helloSignFormModalState.closeModal();
      },
    });

  useEffect(() => {
    if (isSubmittingHelloSignDocument) {
      openSubmittingDocumentModal();
    }
  }, [isSubmittingHelloSignDocument, openSubmittingDocumentModal]);

  if (!isDefined(compositeHelloSignRequirement) || !isDefined(compositeDocumentUploadRequirement)) {
    return <DocumentsErrorPage title="Error loading document" />;
  }

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      <DocumentUploadFooter
        status={status}
        isLoading={
          isCreatingHelloSignSession ||
          isLoadingHelloSignRequirement ||
          isLoadingSignableESignWorkflowSession
        }
        signButtonText={helloSignRequirementCtaText}
        uploadButtonText={documentUploadRequirementCtaText}
        onSignButtonClick={async () => {
          if (isPandaDocSigningEnabled && isDefined(signRequirementId)) {
            pandaDocModalState.openModal();
            loadingPandaDocModalState.openModal();
            void loadSignableESignWorkflowSession({
              compositeParentRequirementId: requirementId,
            });
            return;
          }

          if (
            isDefined(fallbackStaticUrl) &&
            (shouldUseHelloSignFallbackUrl || !isEmbeddedFlowEnabled)
          ) {
            const browserFinishedListener = await addBrowserListener(
              "browserFinished",
              async () => {
                showInfoToast(
                  "If you have signed this document, please check back here later for an update"
                );
                await browserFinishedListener.remove();
              }
            );
            await openInBrowser({ url: fallbackStaticUrl });
          } else {
            helloSignFormModalState.openModal();
            void startHelloSignEmbeddedFlow();
          }
        }}
        onUploadButtonClick={uploadDocument}
        onDeleteButtonClick={deleteDocument}
      />

      <FullScreenDialog modalState={pandaDocModalState}>
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow
            title={name}
            onClickBack={() => {
              pandaDocModalState.closeModal();
            }}
          />
        </PageHeaderWrapper>
        <Box sx={{ height: "100%", overflow: "auto" }}>
          {isDefined(signableESignWorkflowSession?.attributes.esignProviderData?.sessionId) && (
            <PandaDocEmbeddedSigning
              pandaDocSessionId={
                signableESignWorkflowSession?.attributes.esignProviderData.sessionId
              }
              onOpen={loadingPandaDocModalState.closeModal}
              onDocumentLoaded={() => {
                void updateESignWorkflowSession({
                  requirementId: signRequirementId ?? "",
                  sessionId: signableESignWorkflowSession.id,
                  attributesToUpdate: { status: ESignWorkflowSessionStatus.START },
                });
              }}
              onError={() => {
                pandaDocModalState.closeModal();
                logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
                  error: new Error("PandaDoc Embedded Signing SDK Error"),
                  metadata: { requirementId, signRequirementId },
                });
                history.replace(
                  generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
                    requirementId,
                  })
                );
              }}
              onComplete={onPandaDocCompleted}
            />
          )}
        </Box>
        <PandaDocEmbeddedSessionLoadingBottomSheet modalState={loadingPandaDocModalState} />
      </FullScreenDialog>

      <FullScreenDialog modalState={helloSignFormModalState}>
        <Box ref={helloSignIframeContainerRef} sx={{ height: "100%" }} />
      </FullScreenDialog>

      <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
    </PageWrapper>
  );
}
/* eslint-enable unicorn/prevent-abbreviations */
/* eslint-enable max-lines */
